import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    API_ADD_EMPLOYEE,
    API_BRANCHES,
    API_DELETE_EMPLOYEE,
    API_EMPLOYEES,
    API_NEXT_EMPLOYEE_CODE,
    API_UPDATE_EMPLOYEE,
    API_VIEW_EMPLOYEE_DOCUMENT,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { apiJson, apiRequest } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';
import DocumentLinks from '../Common/DocumentLinks';
import './EmployeeMgmt.css';

const initialFormData = {
    id: '',
    emp_generate_id: '',
    emp_name: '',
    emp_branch_id: '',
    emp_contact_no: '',
    emp_address: '',
    emp_uid: '',
    emp_pancard: '',
    emp_role: '',
    is_active: 1,
};

const roleOptions = [
    { value: '1', label: 'Branch Manager' },
    { value: '2', label: 'Staff' },
];

const EmployeeManagement = () => {
    const { canAccess } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialFormData);
    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [filters, setFilters] = useState({
        emp_generate_id: '',
        emp_name: '',
        branch_name: '',
    });
    const [supportsEmpRole, setSupportsEmpRole] = useState(false);
    const [uidFile, setUidFile] = useState(null);
    const [panFile, setPanFile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const loadBranches = async () => {
        const { data } = await apiJson(API_BRANCHES, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        setBranches(data?.data || []);
    };

    const loadEmployees = async () => {
        const { data } = await apiJson(API_EMPLOYEES, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        setEmployees(data?.data || []);
        setSupportsEmpRole(Boolean(data?.supportsEmpRole));
    };

    const loadNextEmployeeCode = async () => {
        const { data } = await apiJson(API_NEXT_EMPLOYEE_CODE, {
            method: 'POST',
            body: JSON.stringify({}),
        });
        return data?.emp_generate_id || '';
    };

    useEffect(() => {
        const loadPageData = async () => {
            try {
                setIsLoading(true);
                setErrorMessage('');
                const [, employeeCode] = await Promise.all([loadBranches(), loadNextEmployeeCode(), loadEmployees()]);
                setFormData((current) => current.id ? current : { ...current, emp_generate_id: employeeCode });
            } catch (error) {
                setErrorMessage('Unable to load employee data right now. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        void loadPageData();
    }, []);

    const filteredEmployees = useMemo(() => employees.filter((employee) => (
        employee.emp_generate_id?.toLowerCase().includes(filters.emp_generate_id.toLowerCase()) &&
        employee.emp_name?.toLowerCase().includes(filters.emp_name.toLowerCase()) &&
        (employee.branch_name || '').toLowerCase().includes(filters.branch_name.toLowerCase())
    )), [employees, filters]);

    const resetForm = () => {
        void loadNextEmployeeCode().then((employeeCode) => {
            setFormData({ ...initialFormData, emp_generate_id: employeeCode });
        }).catch(() => setFormData(initialFormData));
        setUidFile(null);
        setPanFile(null);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!confirmAction(formData.id ? 'update this employee' : 'add this employee')) return;
        setIsSubmitting(true);
        setMessage('');
        setErrorMessage('');

        try {
            const formPayload = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== '') {
                    formPayload.append(key, value);
                }
            });

            if (uidFile) {
                formPayload.append('emp_uid_upload', uidFile);
            }

            if (panFile) {
                formPayload.append('emp_pancard_upload', panFile);
            }

            const response = await apiRequest(formData.id ? API_UPDATE_EMPLOYEE : API_ADD_EMPLOYEE, {
                method: 'POST',
                body: formPayload,
                isFormData: true,
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result?.message || result?.error || 'Unable to save employee');
            }

            setMessage(
                formData.id
                    ? `Employee ${result.emp_generate_id || ''} updated successfully.`
                    : `Employee ${result.emp_generate_id || ''} created successfully.`
            );
            resetForm();
            await loadEmployees();
        } catch (error) {
            setErrorMessage(error.message || 'Unable to save employee right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (employee) => {
        setFormData({
            id: employee.id,
            emp_generate_id: employee.emp_generate_id || '',
            emp_name: employee.emp_name || '',
            emp_branch_id: employee.emp_branch_id || '',
            emp_contact_no: employee.emp_contact_no || '',
            emp_address: employee.emp_address || '',
            emp_uid: employee.emp_uid || '',
            emp_pancard: employee.emp_pancard || '',
            emp_role: employee.emp_role || '',
            is_active: Number(employee.is_active ?? 1),
        });
        setUidFile(null);
        setPanFile(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (employeeId) => {
        if (!confirmAction('archive this employee')) return;
        try {
            const { data } = await apiJson(API_DELETE_EMPLOYEE, {
                method: 'POST',
                body: JSON.stringify({ employee_id: employeeId }),
            });
            setMessage(data?.message || 'Employee archived successfully.');
            await loadEmployees();
            if (String(formData.id) === String(employeeId)) {
                resetForm();
            }
        } catch (error) {
            setErrorMessage('Unable to delete employee. Loan records may still be linked.');
        }
    };

    const getBranchName = (branchId) => {
        const branch = branches.find((currentBranch) => String(currentBranch.id) === String(branchId));
        return branch?.branch_name || 'Unassigned';
    };

    const getRoleLabel = (roleValue) => {
        const role = roleOptions.find((option) => option.value === String(roleValue));
        return role?.label || 'Not Set';
    };

    return (
        <div className="employee-page">
            <section className="employee-hero">
                <div>
                    <p className="employee-eyebrow">Banking Master Data</p>
                    <h1>Employee Management</h1>
                    <p className="employee-subtitle">
                        Add, edit, and safely remove branch employees while keeping role assignment clear.
                    </p>
                </div>
                <div className="employee-hero-note">
                    <strong>{employees.length}</strong>
                    <span>employees currently available</span>
                </div>
            </section>

            {message ? <div className="employee-banner employee-banner-success">{message}</div> : null}
            {errorMessage ? <div className="employee-banner employee-banner-error">{errorMessage}</div> : null}

            <section className="employee-layout">
                <article className="employee-card">
                    <div className="employee-section-header">
                        <div>
                            <h2>{formData.id ? 'Edit Employee' : 'Add Employee'}</h2>
                            <p>Create banking-side employee records without affecting school modules.</p>
                        </div>
                    </div>

                    <form className="employee-form" onSubmit={handleSubmit}>
                        <div className="employee-form-grid">
                            <div>
                                <label htmlFor="emp_generate_id">Employee Code</label>
                                <input id="emp_generate_id" name="emp_generate_id" value={formData.emp_generate_id} onChange={handleChange} placeholder="Generating employee code..." readOnly={!formData.id} />
                            </div>

                            <div>
                                <label htmlFor="emp_name">Employee Name</label>
                                <input id="emp_name" name="emp_name" value={formData.emp_name} onChange={handleChange} placeholder="Enter employee name" required />
                            </div>

                            <div>
                                <label htmlFor="emp_branch_id">Branch</label>
                                <select id="emp_branch_id" name="emp_branch_id" value={formData.emp_branch_id} onChange={handleChange} required>
                                    <option value="">Select Branch</option>
                                    {branches.map((branch) => (
                                        <option key={branch.id} value={branch.id}>{branch.branch_name}</option>
                                    ))}
                                </select>
                            </div>

                            {supportsEmpRole ? (
                                <div>
                                    <label htmlFor="emp_role">Role</label>
                                    <select id="emp_role" name="emp_role" value={formData.emp_role} onChange={handleChange}>
                                        <option value="">Select Role</option>
                                        {roleOptions.map((role) => (
                                            <option key={role.value} value={role.value}>{role.label}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : null}

                            <div>
                                <label htmlFor="is_active">Status</label>
                                <select id="is_active" name="is_active" value={formData.is_active} onChange={handleChange}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="emp_contact_no">Contact No</label>
                                <input id="emp_contact_no" name="emp_contact_no" value={formData.emp_contact_no} onChange={handleChange} placeholder="Enter contact number" />
                            </div>

                            <div>
                                <label htmlFor="emp_uid">UID Number</label>
                                <input id="emp_uid" name="emp_uid" value={formData.emp_uid} onChange={handleChange} placeholder="Enter UID number" />
                            </div>

                            <div>
                                <label htmlFor="emp_pancard">PAN Card</label>
                                <input id="emp_pancard" name="emp_pancard" value={formData.emp_pancard} onChange={handleChange} placeholder="Enter PAN card number" />
                            </div>

                            <div>
                                <label htmlFor="emp_uid_upload">UID Upload</label>
                                <input id="emp_uid_upload" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(event) => setUidFile(event.target.files?.[0] || null)} />
                                <small>PDF, JPG, or PNG up to 5 MB</small>
                            </div>

                            <div className="employee-form-grid-span">
                                <label htmlFor="emp_pancard_upload">PAN Upload</label>
                                <input id="emp_pancard_upload" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(event) => setPanFile(event.target.files?.[0] || null)} />
                                <small>PDF, JPG, or PNG up to 5 MB</small>
                            </div>

                            <div className="employee-form-grid-span">
                                <label htmlFor="emp_address">Address</label>
                                <textarea id="emp_address" name="emp_address" rows="3" value={formData.emp_address} onChange={handleChange} placeholder="Enter employee address" />
                            </div>
                        </div>

                        <div className="employee-actions">
                            {(formData.id ? canAccess('employees', 'edit') : canAccess('employees', 'create')) ? (
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Saving...' : formData.id ? 'Update Employee' : 'Save Employee'}
                                </button>
                            ) : null}
                            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                                Reset
                            </button>
                        </div>
                    </form>
                </article>

                <article className="employee-card">
                    <div className="employee-section-header">
                        <div>
                            <h2>Employee Directory</h2>
                            <p>Review branch allocation and verify role mapping for live banking operations.</p>
                        </div>
                    </div>

                    <div className="employee-filter-grid">
                        <input name="emp_generate_id" value={filters.emp_generate_id} onChange={handleFilterChange} placeholder="Search employee code" />
                        <input name="emp_name" value={filters.emp_name} onChange={handleFilterChange} placeholder="Search employee name" />
                        <input name="branch_name" value={filters.branch_name} onChange={handleFilterChange} placeholder="Search branch name" />
                    </div>

                    {isLoading ? (
                        <div className="dashboard-empty-state">Loading employees...</div>
                    ) : (
                        <div className="employee-table-wrap">
                            <table className="employee-table data-table">
                                <thead>
                                    <tr>
                                        <th>Employee Code</th>
                                        <th>Name</th>
                                        <th>Branch</th>
                                        {supportsEmpRole ? <th>Role</th> : null}
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEmployees.length > 0 ? (
                                        filteredEmployees.map((employee) => (
                                            <tr key={employee.id}>
                                                <td>{employee.emp_generate_id}</td>
                                                <td>{employee.emp_name}</td>
                                                <td>{employee.branch_name || getBranchName(employee.emp_branch_id)}</td>
                                                {supportsEmpRole ? <td>{getRoleLabel(employee.emp_role)}</td> : null}
                                                <td>{Number(employee.is_active) === 1 ? 'Active' : 'Inactive'}</td>
                                                <td>
                                                    <div className="user-table-actions">
                                                        {canAccess('employees', 'view') ? (
                                                            <button type="button" className="btn btn-sm btn-info" onClick={() => setSelectedEmployee(employee)}>
                                                                View Details
                                                            </button>
                                                        ) : null}
                                                        {canAccess('employees', 'edit') ? (
                                                            <button type="button" className="btn btn-sm btn-warning" onClick={() => handleEdit(employee)}>
                                                                Edit
                                                            </button>
                                                        ) : null}
                                                        {canAccess('user_management', 'create') ? (
                                                            <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/users?employee_id=${employee.id}`)}>
                                                                {employee.linked_user_id ? 'Manage Login' : 'Create Login'}
                                                            </button>
                                                        ) : null}
                                                        {canAccess('employees', 'delete') ? (
                                                            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(employee.id)}>
                                                                Delete
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={supportsEmpRole ? 6 : 5} className="employee-table-empty">
                                                No employees found for the current filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </article>
            </section>
            {selectedEmployee ? (
                <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Employee Details</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedEmployee(null)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    {[
                                        ['Employee Code', selectedEmployee.emp_generate_id],
                                        ['Employee Name', selectedEmployee.emp_name],
                                        ['Branch', selectedEmployee.branch_name || getBranchName(selectedEmployee.emp_branch_id)],
                                        ['Role', getRoleLabel(selectedEmployee.emp_role)],
                                        ['Contact No', selectedEmployee.emp_contact_no],
                                        ['UID', selectedEmployee.emp_uid],
                                        ['PAN', selectedEmployee.emp_pancard],
                                        ['Address', selectedEmployee.emp_address],
                                    ].map(([label, value]) => (
                                        <div className="col-md-6" key={label}>
                                            <strong className="d-block text-muted small">{label}</strong>
                                            <span>{value || 'Not provided'}</span>
                                        </div>
                                    ))}
                                    <div className="col-12">
                                        <strong className="d-block text-muted small mb-2">Uploaded Documents</strong>
                                        <DocumentLinks
                                            endpoint={API_VIEW_EMPLOYEE_DOCUMENT}
                                            ownerType="employee"
                                            ownerId={selectedEmployee.id}
                                            documents={[
                                                { field: 'emp_uid_upload_path', label: 'View UID', available: selectedEmployee.has_emp_uid_upload },
                                                { field: 'emp_pancard_upload_path', label: 'View PAN', available: selectedEmployee.has_emp_pancard_upload },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setSelectedEmployee(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default EmployeeManagement;
